import { z } from "zod";
import { RichText } from "../RichText";
import { globalSectionSchema, backgroundStyleEnum } from "../globalProps";
import { SectionShell } from "../SectionShell";

const logoSchema = z.object({
  imageUrl: z.string().optional(),
  imageOriginalUrl: z.string().optional(),
  imageOriginalStorageKey: z.string().optional(),
  imageCrop: z.object({
    x: z.number(), y: z.number(), width: z.number(), height: z.number(), zoom: z.number(),
  }).optional(),
  alt: z.string().default("Partner logo"),
  href: z.string().optional(),
});

export const schema = globalSectionSchema.extend({
  heading: z.string().default("Trusted By"),
  logos: z
    .array(logoSchema)
    .default([
      { alt: "Partner 1" },
      { alt: "Partner 2" },
      { alt: "Partner 3" },
      { alt: "Partner 4" },
      { alt: "Partner 5" },
    ]),
  grayscale: z.boolean().default(true),
  layout: z.enum(["row", "marquee"]).default("row"),
  maxLogoHeight: z.number().int().min(24).max(120).default(48),
  backgroundStyle: backgroundStyleEnum.default("light"),
  /* LogoStrip defaults to compact density to reproduce current py-12/py-16 */
  density: z.enum(["compact", "normal", "spacious"]).default("compact"),
});

export type Props = z.infer<typeof schema>;
export const defaults: Props = schema.parse({});

function LogoItem({ logo, filterClass, maxHeight, itemIndex }: { logo: z.infer<typeof logoSchema>; filterClass: string; maxHeight: number; itemIndex?: number }) {
  const content = (
    <img
      src={logo.imageUrl}
      alt={logo.alt}
      className={filterClass}
      style={{ maxHeight, width: "auto" }}
      loading="lazy"
      data-item-prop={typeof itemIndex === "number" ? `logos.${itemIndex}` : undefined}
    />
  );

  if (logo.href) {
    return (
      <a href={logo.href} className="block shrink-0" target="_blank" rel="noopener noreferrer">
        {content}
      </a>
    );
  }

  return <div className="block shrink-0">{content}</div>;
}

function MarqueeStrip({ entries, filterClass, maxHeight }: { entries: { logo: z.infer<typeof logoSchema>; originalIndex: number }[]; filterClass: string; maxHeight: number }) {
  const duration = Math.min(Math.max(entries.length * 3, 10), 60);
  return (
    <div className="overflow-hidden" style={{ WebkitMaskImage: "linear-gradient(to right, transparent, black 5%, black 95%, transparent)" }}>
      <style>{`@keyframes marquee-scroll{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}@media(prefers-reduced-motion:reduce){.marquee-track{animation-play-state:paused!important}}.marquee-track:focus-within{animation-play-state:paused!important}`}</style>
      <div
        className="marquee-track flex items-center gap-8 @md:gap-12 flex-nowrap"
        style={{
          animation: `marquee-scroll ${duration}s linear infinite`,
          width: "max-content",
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.animationPlayState = "paused"; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.animationPlayState = "running"; }}
      >
        {/* Original set */}
        {entries.map(({ logo, originalIndex }, i) => (
          <LogoItem key={`a-${i}`} logo={logo} filterClass={filterClass} maxHeight={maxHeight} itemIndex={originalIndex} />
        ))}
        {/* Duplicate set for seamless loop */}
        {entries.map(({ logo, originalIndex }, i) => (
          <LogoItem key={`b-${i}`} logo={logo} filterClass={filterClass} maxHeight={maxHeight} itemIndex={originalIndex} />
        ))}
      </div>
    </div>
  );
}

export function Component(props: Props) {
  const result = schema.safeParse(props);
  const safe = result.success ? result.data : defaults;

  // Filter out logos without images -- placeholders are only for the editor.
  // Preserve original indices so data-item-prop points to the correct item.
  const indexedVisibleLogos = safe.logos
    .map((logo, i) => ({ logo, originalIndex: i }))
    .filter(({ logo }) => logo.imageUrl);
  if (indexedVisibleLogos.length === 0) return null;

  const filterClass = safe.grayscale
    ? "grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all duration-300"
    : "";

  return (
    <SectionShell globals={safe}>
        {safe.heading && (
          <RichText
            as="p"
            html={safe.heading}
            className="text-lg text-text-muted text-center mb-8"
            data-prop="heading"
          />
        )}

        {safe.layout === "marquee" ? (
          <MarqueeStrip entries={indexedVisibleLogos} filterClass={filterClass} maxHeight={safe.maxLogoHeight} />
        ) : (
          <div className="flex flex-wrap items-center justify-center gap-8 @md:gap-12">
            {indexedVisibleLogos.map(({ logo, originalIndex }, i) => (
              <LogoItem key={i} logo={logo} filterClass={filterClass} maxHeight={safe.maxLogoHeight} itemIndex={originalIndex} />
            ))}
          </div>
        )}
    </SectionShell>
  );
}
